/**
 * The command layer. Per the rules a turn is exactly one action — play a card,
 * draw a card, move a piece, or pass — so each Action represents a whole turn.
 * Actions are plain serialisable values: they can be logged, sent over a wire,
 * and replayed through the engine to reconstruct any game.
 */
import type { PieceTypeId, PlayerId, Square } from "./types";

export type Action =
  | { readonly type: "DEPLOY"; readonly player: PlayerId; readonly cardId: string; readonly to: Square }
  | { readonly type: "MOVE"; readonly player: PlayerId; readonly from: Square; readonly to: Square }
  // Play an ability card. A relocation card carries `from`/`to`; a revive card
  // carries `reviveType`. Unused fields are null. Validated against the ability
  // registry in the engine.
  | {
      readonly type: "PLAY_CARD";
      readonly player: PlayerId;
      readonly cardId: string;
      readonly from: Square | null;
      readonly to: Square | null;
      readonly reviveType: PieceTypeId | null;
    }
  | { readonly type: "DRAW"; readonly player: PlayerId }
  // Discard a card from hand. Only legal — and then mandatory — while the
  // player's hand is over the limit (a card effect can push it over). Does not
  // end the turn: the player keeps discarding until back at the limit.
  | { readonly type: "DISCARD"; readonly player: PlayerId; readonly cardId: string }
  | { readonly type: "PASS"; readonly player: PlayerId }
  // The side to move has run out of clock time and forfeits the game. Driven by
  // the real-time chess clock, not by a player click, but modelled as an action
  // so the loss is logged and replays like any other turn.
  | { readonly type: "TIMEOUT"; readonly player: PlayerId }
  // A player concedes; the opponent wins immediately. Unlike other actions this
  // may be taken regardless of whose turn it is.
  | { readonly type: "RESIGN"; readonly player: PlayerId }
  // Draw negotiation. Like RESIGN, these may be taken on either side's turn and
  // don't consume the player's action. OFFER_DRAW opens an offer; the opponent
  // answers with ACCEPT_DRAW (game ends in a draw) or DECLINE_DRAW.
  | { readonly type: "OFFER_DRAW"; readonly player: PlayerId }
  | { readonly type: "ACCEPT_DRAW"; readonly player: PlayerId }
  | { readonly type: "DECLINE_DRAW"; readonly player: PlayerId }
  // The Captain's free move — one per turn, and it does NOT end the turn.
  | { readonly type: "CAPTAIN_MOVE"; readonly player: PlayerId; readonly from: Square; readonly to: Square };

export const deploy = (player: PlayerId, cardId: string, to: Square): Action => ({
  type: "DEPLOY",
  player,
  cardId,
  to,
});

export const move = (player: PlayerId, from: Square, to: Square): Action => ({
  type: "MOVE",
  player,
  from,
  to,
});

export const playCard = (
  player: PlayerId,
  cardId: string,
  opts: { from?: Square; to?: Square; reviveType?: PieceTypeId } = {},
): Action => ({
  type: "PLAY_CARD",
  player,
  cardId,
  from: opts.from ?? null,
  to: opts.to ?? null,
  reviveType: opts.reviveType ?? null,
});

export const draw = (player: PlayerId): Action => ({ type: "DRAW", player });

export const discard = (player: PlayerId, cardId: string): Action => ({
  type: "DISCARD",
  player,
  cardId,
});

export const pass = (player: PlayerId): Action => ({ type: "PASS", player });

export const timeout = (player: PlayerId): Action => ({ type: "TIMEOUT", player });

export const resign = (player: PlayerId): Action => ({ type: "RESIGN", player });

export const offerDraw = (player: PlayerId): Action => ({ type: "OFFER_DRAW", player });

export const acceptDraw = (player: PlayerId): Action => ({ type: "ACCEPT_DRAW", player });

export const declineDraw = (player: PlayerId): Action => ({ type: "DECLINE_DRAW", player });

export const captainMove = (player: PlayerId, from: Square, to: Square): Action => ({
  type: "CAPTAIN_MOVE",
  player,
  from,
  to,
});
